import { useFormik } from 'formik';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { postTan } from 'src/api/stoApi';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { authStore } from 'src/stores/AuthStore';
import { awaitUtil } from 'src/util/utils';
import { object, string } from 'yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { IconButton, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';

interface Props {
  open: boolean;
  handleClose: () => void;
  totalAmount: string;
  rentPerShare: string;
  assetId: string;
  payoutId: string;
}

export interface EarningPeriodValues {
  smsTan: string;
}

export const TanModal: FC<Props> = ({ open, handleClose, totalAmount, rentPerShare, assetId, payoutId }) => {
  const { t } = useTranslation();
  const [hasErrors, setHasErrors] = useState<string | boolean>(false);
  const [tanError, setTanError] = useState<string | boolean>(false);
  const [tanErrorText, setTanErrorText] = useState<string>('');
  const [tanStepTwo, setTanStepTwo] = useState<boolean>(false);
  const [verifyingTan, setVerifyingTan] = useState<boolean>(false);
  const navigate = useNavigate();
  const phone = authStore.phone;

  const validationSchemaEarningPeriod = object({
    smsTan: string().min(6, 'Please enter your 6 digit sms tan').required('SMS Tan is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      smsTan: '',
    },
    validationSchema: validationSchemaEarningPeriod,
    validateOnMount: true,
    onSubmit: async (val) => {
      const [response, error] = await awaitUtil(postTan(payoutId, val.smsTan));
      if (response) {
        setTanStepTwo(true);
        setVerifyingTan(false);
      }
      if (error) {
        setTanError(true);
        setTanErrorText(error.message);
        setVerifyingTan(false);
      }
    },
  });
  const sendForm = async () => {
    let errors = false;
    await formik.validateForm();
    if (Object.keys(formik.errors).length > 0) {
      await formik.setTouched({ ...formik.touched, ...(formik.errors as any) });
    }
    // check if any form has errors
    if (formik.errors.smsTan) {
      errors = true;
      setHasErrors(true);
    }
    setHasErrors(hasErrors);
    if (!errors) {
      setVerifyingTan(true);
      await formik.submitForm();
    }
  };

  const handleCloseAfterSuccess = () => {
    // To do navigate to payout overview instead of dashboard
    navigate(`/assets/${assetId}/investorpayout`, { replace: true });
  };

  return (
    <div>
      {/* First Screen, before tan is validated */}
      {!tanStepTwo && (
        <Dialog open={open} onClose={handleClose} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('issuer.rent.tanModal.Confirm')}
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>{t('issuer.rent.tanModal.total', { totalAmount })}</DialogContentText>
            <DialogContentText>{t('issuer.rent.tanModal.rentPerShare', { rentPerShare })}</DialogContentText>
            <DialogContentText>{t('issuer.rent.tanModal.tanSentToPhone', { phone })}</DialogContentText>
            <form onSubmit={formik.handleSubmit}>
              <DefaultTextField label="SMS Tan" fieldName="smsTan" formik={formik} autocomplete="one-time-code" />
            </form>
            {tanError && (
              <Typography color="red">
                {t('issuer.rent.tanModal.ErrorSMSTan')} '{tanErrorText}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              {t('issuer.rent.tanModal.cancel')}
            </Button>
            <LoadingButton variant="contained" loading={verifyingTan} onClick={sendForm} disabled={Boolean(formik.errors.smsTan)}>
              {t('issuer.rent.tanModal.validate')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}
      {/* Second Screen, after tan is validated */}
      {tanStepTwo && (
        <Dialog open={open} onClose={handleCloseAfterSuccess} fullWidth>
          <DialogTitle>
            <Grid container>
              <Typography flexGrow={1} variant="h6">
                {t('issuer.rent.tanModal.titleSuccess')}
              </Typography>
              <IconButton onClick={handleCloseAfterSuccess}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </DialogTitle>
          <DialogContent
            style={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CheckCircleOutlineIcon style={{ fontSize: '120px' }} color="primary" />
            <DialogContentText>{t('issuer.rent.tanModal.success')}</DialogContentText>
            {tanError && (
              <Typography color="red">
                {t('issuer.rent.tanModal.ErrorSMSTan')} {tanErrorText}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleCloseAfterSuccess}>
              {t('issuer.rent.tanModal.closeModal')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};
